import { logException } from '@/sentry';
import { EventBus } from '@/services/eventbus';

// The marker proving a recovery reload already happened lives in the document
// URL rather than in storage: the logout mutation calls sessionStorage.clear(),
// and an expired session logs out on every boot, so a stored guard would be
// wiped exactly when a permanently broken chunk needs it most.
const GUARD_PARAM = 'chunkReload';

// Rejections that mean "the route's JS chunk did not load". The flag is set at
// the import site and is authoritative; name and message are a fallback for
// rejections that reach the router from somewhere else.
// The `<` case is a stale index.html being served in place of a rehashed chunk
// and parsed as JS, which is the most common trigger and is not a ChunkLoadError.
const CHUNK_ERROR_MESSAGE =
  /loading (css )?chunk .* failed|unexpected token '?<|failed to fetch dynamically imported module/i;

export const ROUTE_CHUNK_ERROR_FLAG = 'isRouteChunkError';

// Emitted when a chunk stayed broken across a recovery reload, so the app can
// tell the user rather than leaving the click looking like it did nothing.
export const ROUTE_CHUNK_UNRECOVERABLE = 'route-chunk-unrecoverable';

let recoveryAttempted = false;
// The route the recovery reload was aiming for. Reaching it is what proves the
// reload worked; landing anywhere else does not.
let recoveryTarget = null;

// Tag a route import rejection so the router can tell a missing chunk apart
// from an error thrown inside a navigation guard.
export function markRouteChunkError(error) {
  if (error && typeof error === 'object') {
    error[ROUTE_CHUNK_ERROR_FLAG] = true;
  }
  return error;
}

export function isChunkLoadError(error) {
  if (!error) {
    return false;
  }
  if (error[ROUTE_CHUNK_ERROR_FLAG] === true) {
    return true;
  }
  if (error.name === 'ChunkLoadError') {
    return true;
  }
  return CHUNK_ERROR_MESSAGE.test(String(error.message || ''));
}

/**
 * Reads and strips the marker left by a previous recovery reload. Call once at
 * router setup, before anything reads the query string.
 */
export function consumeReloadMarker(deps = {}) {
  const location = deps.location || window.location;
  const history = deps.history || window.history;
  const params = new URLSearchParams(location.search);

  recoveryAttempted = params.has(GUARD_PARAM);
  if (recoveryAttempted) {
    // The target is carried in the hash, which never reaches the server.
    recoveryTarget = location.hash ? location.hash.slice(1) : null;
    params.delete(GUARD_PARAM);
    const search = params.toString();
    history.replaceState(history.state, '', `${location.pathname}${search ? `?${search}` : ''}${location.hash}`);
  }
  return recoveryAttempted;
}

/**
 * Disarms the guard once the recovery actually succeeded, which only reaching
 * the route we reloaded for proves. A redirect elsewhere (the auth guard sending
 * an expired session to the login page) also completes a navigation, and
 * disarming on that would hand the still-broken route another reload every time
 * the user arrives at it.
 */
export function clearRecoveryMarker(path) {
  if (path === recoveryTarget) {
    recoveryAttempted = false;
    recoveryTarget = null;
  }
}

// Changing the search makes this a real document navigation, so the fresh
// document keeps the target hash. A fragment-only write would not reload at
// all, and vue-router's hash listener reverts it before a reload() lands.
function reloadTo(location, path) {
  const params = new URLSearchParams(location.search);
  params.set(GUARD_PARAM, '1');
  location.replace(`${location.pathname}?${params.toString()}#${path}`);
}

/**
 * Handles an error that aborted a navigation. Returns true if a recovery
 * reload was triggered.
 */
export function handleRouteError(error, path, deps = {}) {
  const location = deps.location || window.location;
  const target = path || '/';

  if (!isChunkLoadError(error)) {
    // Registering onError replaces vue-router's own console.error, so anything
    // else landing here still has to be reported or it disappears entirely.
    logException(error, { tags: { routerError: 'navigation' }, extra: { path: target } });
    return false;
  }

  logException(error, {
    tags: { routerError: 'chunkLoad', chunkLoadRecovery: recoveryAttempted ? 'suppressed' : 'reload' },
    extra: { path: target },
  });

  if (recoveryAttempted) {
    // A reload already failed to fix this session, so the chunk is broken
    // rather than stale. Reloading again would just loop, so surface it instead.
    EventBus.$emit(ROUTE_CHUNK_UNRECOVERABLE, target);
    return false;
  }

  recoveryAttempted = true;
  recoveryTarget = target;
  reloadTo(location, target);
  return true;
}
